import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  Globe, 
  MapPin, 
  Linkedin, 
  Download,
  Building2,
  User,
  Instagram,
  Facebook
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { publicAPI } from '@/lib/api';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const PublicCard = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const response = await publicAPI.getCard(slug);
        setData(response.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Card not found' : 'Failed to load card');
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [slug]);

  useEffect(() => {
    const floatingBadge = document.getElementById('cloudtech-badge');
    if (floatingBadge) {
      floatingBadge.style.display = 'none';
    }
    return () => {
      if (floatingBadge) {
        floatingBadge.style.display = 'inline-flex';
      }
    };
  }, []);

  const handleSaveContact = () => {
    window.location.href = publicAPI.downloadVCard(slug);
  };

  if (loading) {
    return (
      <div className="min-h-screen molecular-bg flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen molecular-bg flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <User className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Card Not Found</h1>
          <p className="text-slate-600">This digital business card doesn't exist or has been deactivated.</p>
        </div>
      </div>
    );
  }

  const { employee, settings } = data;

  const contactActions = [
    { 
      icon: Phone, 
      label: 'CALL', 
      value: employee.phone, 
      href: `tel:${employee.phone}`,
      show: !!employee.phone 
    },
    { 
      icon: Phone, 
      label: 'MOBILE', 
      value: employee.mobile, 
      href: `tel:${employee.mobile}`,
      show: !!employee.mobile 
    },
    { 
      icon: Mail, 
      label: 'EMAIL', 
      value: employee.email, 
      href: `mailto:${employee.email}`,
      show: !!employee.email 
    },
    { 
      icon: Globe, 
      label: 'WEBSITE', 
      value: employee.website || settings.website, 
      href: employee.website || settings.website,
      show: !!(employee.website || settings.website)
    },
    { 
      icon: Linkedin, 
      label: 'LINKEDIN', 
      value: 'View Profile', 
      href: employee.linkedin,
      show: !!employee.linkedin 
    },
    { 
      icon: MapPin, 
      label: 'ADDRESS', 
      value: employee.address || settings.address, 
      href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(employee.address || settings.address)}`,
      show: !!(employee.address || settings.address)
    },
  ];

  return (
    <div className="min-h-screen molecular-bg py-4 min-[380px]:py-8 px-4">
      <div className="max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="glass-card rounded-3xl overflow-hidden shadow-2xl bg-white"
        >
          {/* Header */}
          <div 
            className="h-32 min-[380px]:h-36 relative px-6 py-5"
            style={{ 
              background: `linear-gradient(135deg, ${settings.primary_color || '#0F4C81'} 0%, ${settings.secondary_color || '#00A3C4'} 100%)` 
            }}
          >
            <div className="flex flex-col items-start justify-center h-full">
              <img 
                src={settings.logo_url} 
                alt={settings.company_name}
                className="h-16 min-[380px]:h-20 object-contain"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          </div>

          {/* Profile section */}
          <div className="px-6 pb-6 relative">
            {/* Profile photo - Lowered by 30px */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="absolute -top-[26px] right-6 w-24 h-24 min-[380px]:-top-[34px] min-[380px]:w-32 min-[380px]:h-32 min-[440px]:-top-[50px] min-[440px]:w-[154px] min-[440px]:h-[154px] rounded-2xl border-4 border-white shadow-xl overflow-hidden bg-slate-100"
            >
              {employee.profile_photo ? (
                <img 
                  src={employee.profile_photo} 
                  alt={employee.full_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0F4C81] to-[#00A3C4]">
                  <span className="text-2xl min-[380px]:text-4xl font-bold text-white">
                    {employee.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </span>
                </div>
              )}
            </motion.div>

            {/* Name and title - Increased pt-4 to pt-12 to handle the lower photo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="pt-12 max-w-[calc(100%-110px)] min-[380px]:max-w-[calc(100%-140px)] min-[440px]:max-w-[calc(100%-170px)]"
            >
              <h1 className="text-lg min-[380px]:text-xl font-bold text-slate-900 mb-0.5 text-left break-words leading-tight" data-testid="employee-name">
                {employee.full_name}
              </h1>
                <p className="text-[#00A3C4] font-semibold text-sm min-[380px]:text-base text-left break-words leading-tight" data-testid="employee-title">
                  {employee.job_title}
                </p>
              <div className="flex items-center gap-2 mt-1 text-slate-500 text-xs min-[380px]:text-sm whitespace-nowrap overflow-hidden">
                <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{employee.company_name || settings.company_name}</span>
              </div>
            </motion.div>

            {/* Save Contact Button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-6 mb-6"
            >
              <Button
                onClick={handleSaveContact}
                className="w-full bg-[#009bff] hover:bg-[#313131] text-white rounded-xl text-base py-6 shadow-lg flex items-center justify-center gap-2"
                data-testid="save-contact-btn"
              >
                <Download className="w-5 h-5" />
                Save Contact
              </Button>
            </motion.div>

            {/* Contact actions */}
            <div className="space-y-3">
              {contactActions.filter(a => a.show).map((action, index) => (
                <motion.a
                  key={action.label}
                  href={action.href}
                  target={action.label === 'LINKEDIN' || action.label === 'WEBSITE' ? '_blank' : undefined}
                  rel={action.label === 'LINKEDIN' || action.label === 'WEBSITE' ? 'noopener noreferrer' : undefined}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center p-3 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-slate-100 transition-colors group"
                  data-testid={`action-${action.label.toLowerCase()}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center mr-4 flex-shrink-0 text-[#313131] group-hover:scale-110 transition-transform">
                    <action.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                      {action.label}
                    </p>
                    <p className="text-slate-900 font-medium truncate text-sm min-[380px]:text-base">
                      {action.value}
                    </p>
                  </div>
                </motion.a>
              ))}

              {/* Social Media Icons */}
              {(settings.instagram || settings.facebook || settings.tiktok || settings.linkedin_company) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                  className="mt-6 pt-4 border-t border-slate-100"
                >
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold text-center mb-4">
                    Follow Us
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    {settings.linkedin_company && (
                      <a
                        href={settings.linkedin_company}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-xl bg-[#009bff] flex items-center justify-center text-white hover:scale-110 transition-transform shadow-md"
                        data-testid="social-linkedin"
                      >
                        <Linkedin className="w-6 h-6" />
                      </a>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div 
            className="px-6 py-4 border-t border-slate-100 text-center"
            style={{ backgroundColor: 'rgba(15, 76, 129, 0.03)' }}
          >
            <p className="text-sm text-[#313131] font-medium">
              {settings.tagline || 'Serving Health Since 1978'}
            </p>
          </div>
        </motion.div>

        {/* Company credit */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center mt-6 space-y-3"
        >
          <p className="text-slate-400 text-xs">
            Digital Business Card by {settings.company_name}
          </p>
          <a
            href="https://www.cloudtech.com.cy/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#009bff] text-white text-xs font-semibold hover:bg-[#313131] transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/>
            </svg>
            Made by Cloudtech
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default PublicCard;